import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCountry } from "../contexts/CountryContext";
import { API_URL } from "../lib/supabase";
import { Shield, Clock, ChevronRight, CheckCircle } from "lucide-react";

const QuoteRequestPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, session, acceptedTerms: prevAccepted } = useAuth();
  const { countryCode, country, showToggle } = useCountry();
  const [service, setService] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matchedCount, setMatchedCount] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const isOther = slug === 'other';
  const categorySlug = searchParams.get('category') || '';
  const [formData, setFormData] = useState({
    job_description: '',
    country_code: countryCode,
    city: '',
    suburb: '',
    preferred_date: '',
    preferred_time: '',
    budget: ''
  });

  // Keep form's country in sync if the user changes country via the toggle (preview/dev only)
  useEffect(() => {
    setFormData(prev => prev.country_code === countryCode ? prev : { ...prev, country_code: countryCode });
  }, [countryCode]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=' + encodeURIComponent(`/quote-request/${slug}${categorySlug ? '?category=' + categorySlug : ''}`));
      return;
    }
    if (isOther) {
      fetchCategory();
    } else {
      fetchService();
    }
  }, [slug, isAuthenticated]);

  const fetchService = async () => {
    try {
      const response = await fetch(`${API_URL}/services/${slug}`);
      const data = await response.json();
      setService(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategory = async () => {
    try {
      if (categorySlug) {
        const response = await fetch(`${API_URL}/categories`);
        const cats = await response.json();
        const cat = cats.find(c => c.slug === categorySlug);
        if (cat) setCategoryName(cat.name);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    // If user hasn't previously accepted terms, require checkbox
    if (!prevAccepted && !acceptTerms) {
      setSubmitError('You must agree to the Terms of Service and Privacy Policy before submitting.');
      setSubmitting(false);
      return;
    }

    try {
      const body = { ...formData };
      // Send acceptance flag if user is opting in this session
      if (acceptTerms) body.accepted_terms = true;
      if (isOther) {
        body.category_slug = categorySlug;
      } else {
        body.service_id = service?.id;
      }

      const response = await fetch(`${API_URL}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setMatchedCount(data.matched_providers || 0);
        setSubmitted(true);
      } else {
        const err = await response.json().catch(() => ({}));
        setSubmitError(err.detail || 'Failed to submit your quote request. Please check your details and try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitError('A network error occurred. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!isOther && !service) return <div className="error">Service not found</div>;

  const displayName = isOther ? (categoryName ? `Other ${categoryName} Service` : 'Custom Service Request') : service.name;

  // ── Confirmation Screen ──
  if (submitted) {
    const noProviders = matchedCount === 0;
    return (
      <div className="page quote-request-page" data-testid="quote-confirmation">
        <div className="quote-form-container" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: noProviders ? '#FEF3C7' : '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            {noProviders ? <Clock size={32} style={{ color: '#92400e' }} /> : <CheckCircle size={32} style={{ color: '#065f46' }} />}
          </div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }} data-testid="quote-confirmation-title">
            {noProviders ? 'Quote Request Saved' : 'Quote Request Submitted'}
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24, maxWidth: 440, margin: '0 auto 24px' }}>
            {noProviders ? (
              <span data-testid="no-providers-message">
                We've saved your request for <strong>{displayName}</strong>, but there are <strong>limited providers currently available</strong> in your area for this service. We'll notify you by email as soon as a verified provider joins.
              </span>
            ) : (
              <>Your request for <strong>{displayName}</strong> has been sent to {matchedCount} verified provider{matchedCount > 1 ? 's' : ''} in your area.</>
            )}
          </p>
          <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', padding: '20px 24px', textAlign: 'left', maxWidth: 440, margin: '0 auto 24px' }}>
            <p style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-dark)' }}>What happens next?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(noProviders ? [
                'Your request is saved and visible in "My Quotes"',
                'We\'ll email you as soon as a verified provider for this service joins HandyHub',
                'You can also browse other services or contact us at ' + country.supportEmail,
              ] : [
                'Providers will review your request and respond with a quote',
                'You\'ll be notified by email when a provider responds',
                'Compare quotes and choose the best provider for your job',
              ]).map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, fontSize: '0.8125rem', color: 'var(--text-dark)' }}>
                  <span style={{ fontWeight: 700, color: 'var(--amber)', minWidth: 20 }}>{i + 1}.</span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/my-quotes" className="btn-primary" data-testid="view-my-quotes-btn">View My Quotes</Link>
            <Link to="/services" className="btn-outline" data-testid="browse-more-btn">Browse More Services</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page quote-request-page">
      <div className="breadcrumb">
        <Link to="/services">Services</Link>
        <ChevronRight size={16} />
        {isOther ? (
          <>
            {categorySlug && <><Link to={`/categories/${categorySlug}`}>{categoryName || categorySlug}</Link><ChevronRight size={16} /></>}
            <span>Request Quote</span>
          </>
        ) : (
          <>
            <Link to={`/services/${service.slug}`}>{service.name}</Link>
            <ChevronRight size={16} />
            <span>Request Quote</span>
          </>
        )}
      </div>

      <div className="quote-form-container">
        <h1>Request a Quote</h1>
        <p className="quote-service">For: <strong>{displayName}</strong></p>

        {/* Trust messaging */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }} data-testid="trust-badges">
          {[
            { icon: <Shield size={14} />, text: 'Free, no obligation' },
            { icon: <CheckCircle size={14} />, text: 'Verified providers' },
            { icon: <Clock size={14} />, text: 'Responses within 24hrs' },
          ].map((b, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#065f46', background: '#d1fae5', padding: '4px 10px', borderRadius: 20, fontWeight: 500 }}>
              {b.icon} {b.text}
            </span>
          ))}
        </div>

        {isOther && (
          <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', fontSize: '0.8125rem', color: 'var(--text-dark)' }}>
            Describe the service you need below. Our team will match you with qualified professionals in your area.
          </div>
        )}

        {submitError && (
          <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '0.8125rem', color: '#DC2626' }} data-testid="submit-error">
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="quote-form" data-testid="quote-form">
          <div className="form-group">
            <label>Describe your job *</label>
            <textarea
              value={formData.job_description}
              onChange={(e) => setFormData({...formData, job_description: e.target.value})}
              placeholder="Please describe what you need done, including any specific requirements..."
              rows={5}
              required
              data-testid="job-description"
            />
          </div>

          <div className="form-row">
            {showToggle ? (
              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData({...formData, country_code: e.target.value})}
                  data-testid="country-select"
                >
                  <option value="NZ">New Zealand</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
            ) : (
              <input type="hidden" data-testid="country-locked" value={formData.country_code} readOnly />
            )}
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="Enter your city or suburb"
                required
                data-testid="city-select"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Suburb (Optional)</label>
            <input
              type="text"
              value={formData.suburb}
              onChange={(e) => setFormData({...formData, suburb: e.target.value})}
              placeholder="Enter your suburb"
              data-testid="suburb-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Preferred Date (Optional)</label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                data-testid="date-input"
              />
            </div>
            <div className="form-group">
              <label>Preferred Time (Optional)</label>
              <select
                value={formData.preferred_time}
                onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                data-testid="time-select"
              >
                <option value="">Any time</option>
                <option value="morning">Morning (8am - 12pm)</option>
                <option value="afternoon">Afternoon (12pm - 5pm)</option>
                <option value="evening">Evening (5pm - 8pm)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Budget (Optional)</label>
            <select
              value={formData.budget}
              onChange={(e) => setFormData({...formData, budget: e.target.value})}
              data-testid="budget-select"
            >
              <option value="">Not sure / flexible</option>
              <option value="under_100">Under $100</option>
              <option value="100_250">$100 - $250</option>
              <option value="250_500">$250 - $500</option>
              <option value="500_1000">$500 - $1,000</option>
              <option value="1000_plus">$1,000+</option>
            </select>
          </div>

          {!prevAccepted && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="quote-accept-terms"
                checked={acceptTerms}
                onChange={(e) => { setAcceptTerms(e.target.checked); e.target.setCustomValidity(''); }}
                onInvalid={(e) => e.target.setCustomValidity('Please read and accept the Terms of Service and Privacy Policy by ticking the box.')}
                required
                data-testid="quote-accept-terms-checkbox"
                style={{ marginTop: '3px', cursor: 'pointer', width: 'auto' }}
              />
              <label htmlFor="quote-accept-terms" style={{ fontSize: '0.8125rem', color: 'var(--text-dark)', lineHeight: 1.5, cursor: 'pointer' }}>
                I agree to the{' '}
                <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Privacy Policy</Link>.
              </label>
            </div>
          )}

          <button 
            type="submit" 
            className="btn-primary btn-large"
            disabled={submitting}
            data-testid="submit-quote"
          >
            {submitting ? 'Submitting...' : 'Submit Quote Request — Free'}
          </button>
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
            No obligation. Compare quotes and choose the best provider.
          </p>
          <div
            data-testid="quote-disclaimer"
            style={{ marginTop: '14px', padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius-md)' }}
          >
            <p style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.55, color: '#92400e', fontStyle: 'italic' }}>
              HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};


export default QuoteRequestPage;
